/**
 * Insurance Validation Utilities
 * Provides validation functions for insurance-related data
 */

export interface ValidationResult {
  isValid: boolean;
  error: string;
}

/**
 * Validate Medicare Beneficiary Identifier (MBI)
 * Format: 1EG4-TE5-MK73 (11 characters, specific character rules)
 * - Positions 1, 4, 7, 10: Numeric (0-9)
 * - Positions 2, 5, 8, 11: Alphabetic (A-Z, excluding S, L, O, I, B, Z)
 * - Positions 3, 6, 9: Alphanumeric (0-9 or A-Z, excluding S, L, O, I, B, Z)
 */
export const validateMBI = (mbi: string): ValidationResult => {
  if (!mbi || mbi.trim() === '') {
    return {
      isValid: false,
      error: 'Medicare Beneficiary Identifier (MBI) is required'
    };
  }

  // Remove hyphens for validation
  const cleanMBI = mbi.replace(/-/g, '').toUpperCase();

  if (cleanMBI.length !== 11) {
    return {
      isValid: false,
      error: 'MBI must be 11 characters (format: 1EG4-TE5-MK73)'
    };
  }

  // Characters not allowed in MBI
  const excludedChars = ['S', 'L', 'O', 'I', 'B', 'Z'];

  // Validate position 1, 4, 7, 10 (must be numeric)
  const numericPositions = [0, 3, 6, 9];
  for (const pos of numericPositions) {
    if (!/\d/.test(cleanMBI[pos])) {
      return {
        isValid: false,
        error: `MBI position ${pos + 1} must be numeric (0-9)`
      };
    }
  }

  // Validate position 2, 5, 8, 11 (must be alphabetic, excluding certain letters)
  const alphaPositions = [1, 4, 7, 10];
  for (const pos of alphaPositions) {
    const char = cleanMBI[pos];
    if (!/[A-Z]/.test(char)) {
      return {
        isValid: false,
        error: `MBI position ${pos + 1} must be alphabetic (A-Z)`
      };
    }
    if (excludedChars.includes(char)) {
      return {
        isValid: false,
        error: `MBI position ${pos + 1} cannot contain ${char} (excluded: S, L, O, I, B, Z)`
      };
    }
  }

  // Validate position 3, 6, 9 (must be alphanumeric, excluding certain letters)
  const alphanumericPositions = [2, 5, 8];
  for (const pos of alphanumericPositions) {
    const char = cleanMBI[pos];
    if (!/[A-Z0-9]/.test(char)) {
      return {
        isValid: false,
        error: `MBI position ${pos + 1} must be alphanumeric (0-9 or A-Z)`
      };
    }
    if (excludedChars.includes(char)) {
      return {
        isValid: false,
        error: `MBI position ${pos + 1} cannot contain ${char} (excluded: S, L, O, I, B, Z)`
      };
    }
  }

  return {
    isValid: true,
    error: ''
  };
};

/** Strip spaces and dashes from a Medicaid ID as typed. */
export const cleanMedicaidId = (id: string): string => String(id ?? '').replace(/[\s-]/g, '');

/** Pre-Phase 7 values in the old TX-########## format (shown with a re-enter tag, never rejected untouched). */
export const isLegacyMedicaidId = (id?: string): boolean => /^TX-?\d{10}$/i.test(String(id ?? '').replace(/\s/g, ''));

/**
 * Validate a Texas Medicaid ID: 9 digits (REQ-INS-02). Spaces and dashes are ignored.
 */
export const validateMedicaidID = (id: string): ValidationResult => {
  if (!id || id.trim() === '') {
    return { isValid: false, error: 'Medicaid ID is required' };
  }
  if (!/^\d{9}$/.test(cleanMedicaidId(id))) {
    return { isValid: false, error: 'Medicaid ID must be 9 digits' };
  }
  return { isValid: true, error: '' };
};

/**
 * Validate phone number
 * Accepts 10-digit phone numbers with or without formatting
 */
export const validatePhoneNumber = (phone: string): ValidationResult => {
  if (!phone || phone.trim() === '') {
    return {
      isValid: false,
      error: 'Phone number is required'
    };
  }

  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');

  if (digits.length !== 10) {
    return {
      isValid: false,
      error: 'Phone number must be 10 digits'
    };
  }

  // Check if first digit is valid (2-9)
  if (digits[0] === '0' || digits[0] === '1') {
    return {
      isValid: false,
      error: 'Phone number cannot start with 0 or 1'
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Format phone number to (XXX) XXX-XXXX
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');

  // Format based on length
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Truncate to 10 digits
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Validate date comparison
 * Ensures date1 meets the specified rule relative to date2
 */
export const validateDateComparison = (
  date1: string,
  date2: string,
  rule: 'after' | 'before' | 'same-or-after' | 'same-or-before',
  fieldName1: string = 'Date 1',
  fieldName2: string = 'Date 2'
): ValidationResult => {
  if (!date1 || !date2) {
    return { isValid: true, error: '' }; // Skip validation if either date is missing
  }

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Check for invalid dates
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    return {
      isValid: false,
      error: 'Invalid date format'
    };
  }

  switch (rule) {
    case 'after':
      if (d1 <= d2) {
        return {
          isValid: false,
          error: `${fieldName1} must be after ${fieldName2}`
        };
      }
      break;
    case 'before':
      if (d1 >= d2) {
        return {
          isValid: false,
          error: `${fieldName1} must be before ${fieldName2}`
        };
      }
      break;
    case 'same-or-after':
      if (d1 < d2) {
        return {
          isValid: false,
          error: `${fieldName1} must be on or after ${fieldName2}`
        };
      }
      break;
    case 'same-or-before':
      if (d1 > d2) {
        return {
          isValid: false,
          error: `${fieldName1} must be on or before ${fieldName2}`
        };
      }
      break;
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate required fields based on coverage type
 */
/** Medicaid programs that require an MCO (REQ-INS-02). */
export const MCO_REQUIRED_PROGRAMS = ['STAR_KIDS', 'STAR_PLUS'];

export const validateRequiredFields = (
  coverage: any,
  coverageType: 'Private' | 'Medicare' | 'Medicaid' | 'Other',
  options: { legacyIdUnchanged?: boolean } = {}
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Payer name is required for Private and Other; for Medicare/Medicaid it is set by the system
  if (coverageType === 'Private' || coverageType === 'Other') {
    if (!coverage.payerName || coverage.payerName.trim() === '') {
      errors.payerName = coverageType === 'Other' ? 'Payer name is required' : 'Insurance Company Name is required';
    }
  }

  // Both dates are inclusive: a termination date may equal the effective date
  if (coverage.effectiveDate && coverage.terminationDate && coverage.terminationDate < coverage.effectiveDate) {
    errors.terminationDate = 'Termination date cannot be before effective date';
  }

  // Phones are optional (Phase 7); a value given must still be a 10-digit number
  for (const field of ['claimsPhone', 'priorAuthPhone', 'mcoCustomerServicePhone', 'mcoPriorAuthPhone', 'maCustomerServicePhone', 'maPriorAuthPhone', 'supplementalPhone']) {
    if (coverage[field]) {
      const phoneValidation = validatePhoneNumber(coverage[field]);
      if (!phoneValidation.isValid) errors[field] = phoneValidation.error;
    }
  }

  // Policyholder (CMS-1500 boxes 4/7/11): required when the patient is not the policyholder
  if ((coverageType === 'Private' || coverageType === 'Other') && coverage.relationshipToSubscriber && coverage.relationshipToSubscriber !== 'SELF') {
    if (!coverage.subscriberName || coverage.subscriberName.trim() === '') {
      errors.subscriberName = 'Policyholder name is required when the patient is not the policyholder';
    }
    if (!coverage.subscriberDOB) {
      errors.subscriberDOB = 'Policyholder date of birth is required when the patient is not the policyholder';
    }
  }

  if (!coverage.effectiveDate) {
    errors.effectiveDate = 'Effective date is required';
  }

  if (!coverage.cobOrder) {
    errors.cobOrder = 'Coverage order (Primary/Secondary/Tertiary) is required';
  }

  // Coverage-specific validation
  if (coverageType === 'Medicare') {
    if (!coverage.medicareMBI || coverage.medicareMBI.trim() === '') {
      errors.medicareMBI = 'Medicare Beneficiary Identifier (MBI) is required';
    } else {
      const mbiValidation = validateMBI(coverage.medicareMBI);
      if (!mbiValidation.isValid) {
        errors.medicareMBI = mbiValidation.error;
      }
    }

    // Part A/B date validation
    if (!coverage.partAEffectiveDate) {
      errors.partAEffectiveDate = 'Part A effective date is required';
    }

    if (!coverage.partBEffectiveDate) {
      errors.partBEffectiveDate = 'Part B effective date is required';
    }

    // Part B must be >= Part A
    if (coverage.partAEffectiveDate && coverage.partBEffectiveDate) {
      const dateValidation = validateDateComparison(
        coverage.partBEffectiveDate,
        coverage.partAEffectiveDate,
        'same-or-after',
        'Part B Effective Date',
        'Part A Effective Date'
      );
      if (!dateValidation.isValid) {
        errors.partBEffectiveDate = dateValidation.error;
      }
    }

    // Termination date validations
    if (coverage.partATerminationDate && coverage.partAEffectiveDate) {
      const validation = validateDateComparison(
        coverage.partATerminationDate,
        coverage.partAEffectiveDate,
        'after',
        'Part A Termination Date',
        'Part A Effective Date'
      );
      if (!validation.isValid) {
        errors.partATerminationDate = validation.error;
      }
    }

    if (coverage.partBTerminationDate && coverage.partBEffectiveDate) {
      const validation = validateDateComparison(
        coverage.partBTerminationDate,
        coverage.partBEffectiveDate,
        'after',
        'Part B Termination Date',
        'Part B Effective Date'
      );
      if (!validation.isValid) {
        errors.partBTerminationDate = validation.error;
      }
    }

    // Medicare Advantage validation
    if (coverage.hasMedicareAdvantage === true) {
      if (!coverage.maPlanName || coverage.maPlanName.trim() === '') {
        errors.maPlanName = 'Medicare Advantage plan name is required';
      }

      if (!coverage.maPlanType) {
        errors.maPlanType = 'Medicare Advantage plan type is required';
      }

    }

    // Part D validation
    if (coverage.hasPartD === true) {
      if (!coverage.partDPlanName || coverage.partDPlanName.trim() === '') {
        errors.partDPlanName = 'Part D plan name is required';
      }

      if (!coverage.partDPlanID || coverage.partDPlanID.trim() === '') {
        errors.partDPlanID = 'Part D Plan ID is required when Part D coverage is selected';
      }

      if (!coverage.partDEffectiveDate) {
        errors.partDEffectiveDate = 'Part D effective date is required';
      }
    }

    // Medigap validation
    if (coverage.hasMedigap === true) {
      if (!coverage.supplementalPlanName || coverage.supplementalPlanName.trim() === '') {
        errors.supplementalPlanName = 'Medigap plan name is required';
      }

      if (!coverage.supplementalPolicyNumber || coverage.supplementalPolicyNumber.trim() === '') {
        errors.supplementalPolicyNumber = 'Medigap policy number is required';
      }

    }
  } else if (coverageType === 'Medicaid') {
    if (!coverage.medicaidIdNumber || coverage.medicaidIdNumber.trim() === '') {
      errors.medicaidIdNumber = 'Medicaid ID is required';
    } else if (!options.legacyIdUnchanged) {
      const idValidation = validateMedicaidID(coverage.medicaidIdNumber);
      if (!idValidation.isValid) {
        errors.medicaidIdNumber = idValidation.error;
      }
    }

    if (!coverage.medicaidType) {
      errors.medicaidType = 'Medicaid type is required';
    }

    // State of eligibility defaults to the agency's state on the server; county is optional

    // MCO: required for STAR Kids / STAR+PLUS; "Other" needs the MCO name typed in (REQ-INS-02/03)
    if (MCO_REQUIRED_PROGRAMS.includes(coverage.medicaidType) && !coverage.medicaidMCO) {
      errors.medicaidMCO = 'MCO is required for STAR Kids and STAR+PLUS';
    }
    if (coverage.medicaidMCO === 'OTHER' && (!coverage.medicaidMCOOther || coverage.medicaidMCOOther.trim() === '')) {
      errors.medicaidMCOOther = 'Type the MCO name';
    }
  } else if (coverageType === 'Private') {
    if (!coverage.memberId || coverage.memberId.trim() === '') {
      errors.memberId = 'Policy/Member ID is required';
    }

    if (!coverage.requiresPriorAuth) {
      errors.requiresPriorAuth = 'Prior authorization requirement must be specified';
    }
  }

  return errors;
};

/**
 * Generate user-friendly error messages
 */
export const getErrorMessage = (fieldName: string, errorType: string): string => {
  const messages: Record<string, string> = {
    required: `${fieldName} is required`,
    invalid_format: `${fieldName} has an invalid format`,
    invalid_mbi: 'Invalid Medicare Beneficiary Identifier format',
    invalid_medicaid_id: 'Invalid Medicaid ID format',
    invalid_phone: 'Invalid phone number format',
    date_comparison: 'Date validation failed'
  };

  return messages[errorType] || `Validation error for ${fieldName}`;
};
